import VueCookies from 'vue-cookies'
import axios from 'axios'
import router from '@/router/router'
import $const from '@/utils/const'
import store from '@/store/store'
import $http from '@/plugins/axios'
import {BASE_PATH} from '@/utils/base-path'

const currentProjectIdKey = 'currentProject'
const jwtTokenKey = 'jwtToken'
const refreshTokenKey = 'jwtRefreshToken'
const refreshLockKey = 'jwtRefreshLock'
const REFRESH_LOCK_TTL = 10000 // 10 seconds

// BroadcastChannel for cross-tab communication (fallback if browser doesn't support)
const authChannel = typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('auth_channel')
    : null

const AuthService = {
    _useFallbackStorage: false,

    getToken() {
        return localStorage.getItem(jwtTokenKey) ?? ''
    },
    setToken(token) {
        localStorage.setItem(jwtTokenKey, token)
    },
    removeToken() {
        localStorage.removeItem(jwtTokenKey)
    },
    getRefreshToken() {
        if (this._useFallbackStorage) {
            return localStorage.getItem(refreshTokenKey) ?? ''
        }
        return sessionStorage.getItem(refreshTokenKey) ?? ''
    },
    setRefreshToken(token) {
        if (token) {
            if (this._useFallbackStorage) {
                localStorage.setItem(refreshTokenKey, token)
            } else {
                sessionStorage.setItem(refreshTokenKey, token)
            }
        }
    },
    removeRefreshToken() {
        if (this._useFallbackStorage) {
            localStorage.removeItem(refreshTokenKey)
        } else {
            sessionStorage.removeItem(refreshTokenKey)
        }
    },
    getNewAccessToken() {
        if (!this._acquireRefreshLock()) {
            // Another tab is refreshing — return current token
            // BroadcastChannel will sync the new token from that tab
            return Promise.resolve({ data: { access: this.getToken() } })
        }
        const refreshToken = this.getRefreshToken()
        if (!refreshToken) {
            this._releaseRefreshLock()
            return Promise.reject(new Error('No refresh token available'))
        }
        return $http.post('/refresh/', {"refresh": refreshToken})
            .then(result => {
                this._releaseRefreshLock()
                return result
            })
            .catch(err => {
                this._releaseRefreshLock()
                throw err
            })
    },
    getCurrentProjectId(email) {
        if (email && VueCookies.get(`${email}_${currentProjectIdKey}`)) {
            return VueCookies.get(`${email}_${currentProjectIdKey}`)
        }
        return ''
    },
    setCurrentProjectId(email, projectId) {
        return VueCookies.set(`${email}_${currentProjectIdKey}`, projectId)
    },
    decodeJWT(token) {
        // copied from here
        // https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library
        let base64Url = token.split('.')[1]
        let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        let jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        }).join(''))
        return JSON.parse(jsonPayload)
    },
    // all in one authenticate function for easy use
    signIn() {
        switch (store.state.authenticationService) {
            case $const.authenticationService.SELF:
                this.signInSelf()
                break
            case $const.authenticationService.FPT_ID:
                this.signInFptId()
                break
            default:
                router.push('/authentication')
        }
    },
    signOut() {
        this.removeToken()
        this.removeRefreshToken()
        switch (store.state.authenticationService) {
            case $const.authenticationService.SELF:
                this.signOutSelf()
                break
            case $const.authenticationService.FPT_ID:
                this.signOutFptId()
                break
            default:
                router.push('/authentication')
        }
    },
    // authenticate self service
    signInSelf() {
        router.push('/authentication/self')
    },
    signInSelfToken(email, password) {
        return axios.post(
            process.env.VUE_APP_BASE_API_URL + '/token/',
            {email, password}
        )
    },
    signOutSelf() {
        router.push('/authentication/self')
    },
    // authenticate fpt-id service
    signInFptId() {
        window.location.href = process.env.VUE_APP_AUTHORIZATION_END_POINT + '?' + this.createFptIdUrlParams()
    },
    signOutFptId() {
        window.location.href = `${process.env.VUE_APP_END_SESSION_END_POINT}?redirect_uri=${window.location.origin}${BASE_PATH}`
    },
    signOutJWTToken() {
        return $http.post('/logout/', {"refresh_token": this.getRefreshToken()})
    },
    createFptIdUrlParams() {
        return `client_id=${process.env.VUE_APP_AUTHORIZATION_CLIENT_ID}` +
            `&redirect_uri=${window.location.origin}${BASE_PATH}authentication/fpt-id/success/` +
            `&state=${Math.random().toString(36).substring(2)}` +
            `&response_type=code` +
            `&scope=openid+profile+email+phone`
    },

    // === REFRESH MUTEX (localStorage-based, shared across tabs) ===
    _acquireRefreshLock() {
        const now = Date.now()
        const lockValue = localStorage.getItem(refreshLockKey)
        if (lockValue) {
            const lockTime = parseInt(lockValue, 10)
            if (now - lockTime < REFRESH_LOCK_TTL) {
                return false // another tab holds the lock
            }
        }
        localStorage.setItem(refreshLockKey, now.toString())
        // Double-check: re-read to handle race between two tabs writing simultaneously
        // Last writer wins, first writer sees mismatch and backs off
        return localStorage.getItem(refreshLockKey) === now.toString()
    },

    _releaseRefreshLock() {
        localStorage.removeItem(refreshLockKey)
    },

    // === BROADCAST (cross-tab communication) ===
    broadcastTokenUpdate() {
        if (authChannel) {
            authChannel.postMessage({
                type: 'TOKEN_UPDATE',
                accessToken: this.getToken(),
                refreshToken: this.getRefreshToken()
            })
        }
    },

    broadcastLogout() {
        if (authChannel) {
            authChannel.postMessage({ type: 'LOGOUT' })
        }
    },

    // === CROSS-TAB SYNC INITIALIZATION ===
    initCrossTabSync() {
        if (authChannel) {
            this._initBroadcastChannel()
        } else {
            // Fallback for browsers without BroadcastChannel support
            this._initStorageEventFallback()
        }
    },

    _initBroadcastChannel() {
        authChannel.onmessage = (event) => {
            const { type } = event.data
            switch (type) {
                case 'TOKEN_UPDATE':
                    if (event.data.refreshToken) {
                        sessionStorage.setItem(refreshTokenKey, event.data.refreshToken)
                        store.commit('user/REFRESH_TOKEN', event.data.refreshToken)
                    }
                    if (event.data.accessToken) {
                        this.setToken(event.data.accessToken)
                        store.commit('user/TOKEN', event.data.accessToken)
                    }
                    break
                case 'LOGOUT':
                    sessionStorage.removeItem(refreshTokenKey)
                    this.removeToken()
                    store.commit('user/TOKEN', '')
                    store.commit('user/REFRESH_TOKEN', '')
                    store.commit('user/PROFILE', {})
                    this.signIn()
                    break
                case 'TOKEN_REQUEST': {
                    const rt = this.getRefreshToken()
                    if (rt) {
                        authChannel.postMessage({
                            type: 'TOKEN_RESPONSE',
                            accessToken: this.getToken(),
                            refreshToken: rt
                        })
                    }
                    break
                }
                case 'TOKEN_RESPONSE':
                    // Handled in _requestTokensFromPeers
                    break
            }
        }

        // If we have access token but no refresh token, request from other tabs
        if (this.getToken() && !this.getRefreshToken()) {
            this._requestTokensFromPeers()
        }
    },

    _requestTokensFromPeers() {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                resolve(false) // No tab responded within 1 second
            }, 1000)

            const handler = (event) => {
                if (event.data.type === 'TOKEN_RESPONSE') {
                    clearTimeout(timeout)
                    authChannel.removeEventListener('message', handler)
                    if (event.data.refreshToken) {
                        sessionStorage.setItem(refreshTokenKey, event.data.refreshToken)
                        store.commit('user/REFRESH_TOKEN', event.data.refreshToken)
                    }
                    if (event.data.accessToken) {
                        this.setToken(event.data.accessToken)
                        store.commit('user/TOKEN', event.data.accessToken)
                    }
                    resolve(true)
                }
            }
            authChannel.addEventListener('message', handler)
            authChannel.postMessage({ type: 'TOKEN_REQUEST' })
        })
    },

    // Fallback for browsers without BroadcastChannel — use localStorage for refresh token
    _initStorageEventFallback() {
        this._useFallbackStorage = true

        window.addEventListener('storage', (event) => {
            if (event.key === jwtTokenKey) {
                if (!event.newValue) {
                    store.commit('user/TOKEN', '')
                    store.commit('user/REFRESH_TOKEN', '')
                    store.commit('user/PROFILE', {})
                    this.signIn()
                } else if (event.newValue !== store.state.user.token) {
                    store.commit('user/TOKEN', event.newValue)
                }
            }
            if (event.key === refreshTokenKey) {
                if (event.newValue && event.newValue !== store.state.user.refreshToken) {
                    store.commit('user/REFRESH_TOKEN', event.newValue)
                }
            }
        })
    }
}

export default AuthService
